import { Option } from '@app/types';
import { ReactElement, useMemo } from 'react';
import {
  CSSObjectWithLabel,
  default as ReactSelect,
  Props as ReactSelectProps
} from 'react-select';
import styles from './select.module.scss';

export type SelectProps<T> = (
  Pick<ReactSelectProps<Option<T>>, (
    'isLoading'
    | 'loadingMessage'
    | 'name'
    | 'noOptionsMessage'
    | 'onBlur'
    | 'onFocus'
    | 'styles'
  )>
  & {
    onChange: (newValue: T | null) => void;
    options: Option<T>[] | null | undefined;
    value: T | null | undefined;
  }
);

export const Select = function<T>({
  onChange,
  options,
  value: optionValue,
  ...reactSelectProps
}: SelectProps<T>): ReactElement<any, any> | null {
  const selectedOption = useMemo(() => {
    return options?.find(({ value }) => {
      return value === optionValue;
    });
  }, [options, optionValue]);
  return (
    <ReactSelect<Option<T>>
      {...reactSelectProps}
      classNames={{
        menu: () => {
          return `ui-select-menu ${styles.menu}`;
        }
      }}
      styles={{
        ...reactSelectProps.styles,
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderColor: (state.isFocused ? 'black' : '#e1ddd8') + ' !important',
          borderRadius: 'var(--border-radius-m)',
          boxShadow: 'none',
          outline: 'none',
          ...reactSelectProps.styles?.control
        }),
        option: (baseStyles, { isFocused, isSelected }) => {
          let backgroundColor = '';
          if (isFocused) {
            backgroundColor = 'var(--color-highlight-soft)';
          } else if (isSelected) {
            backgroundColor = 'var(--color-highlight-hover)';
          }

          return {
            ...baseStyles,
            backgroundColor,
            color: 'inherit'
          };
        },
        indicatorSeparator: (baseStyles, state) => ({
          ...baseStyles,
          backgroundColor: state.isFocused ? 'black' : '#e1ddd8',
          ...reactSelectProps.styles?.indicatorSeparator
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          color: 'var(--color-input-placeholder)',
          fontStyle: 'italic',
          opacity: '0.5',
          ...reactSelectProps.styles?.placeholder
        })
      }}
      getOptionLabel={({ label }) => {
        return label;
      }}
      tabSelectsValue={false}
      onChange={(option) => {
        onChange(option?.value ?? null);
      }}
      options={options ?? undefined}
      placeholder="Search..."
      value={selectedOption}
    />
  );
};

Select.styleAddDot = <T extends CSSObjectWithLabel>(style: T, color = 'transparent'): T => {
  return {
    ...style,
    alignItems: 'center',
    display: 'flex',
    overflow: 'visible',
    ':after': {
      backgroundColor: color,
      borderRadius: 'var(--border-radius-l)',
      content: '" "',
      display: 'block',
      flexShrink: 0,
      height: '8px',
      marginLeft: 5,
      width: '8px'
    }
  };
};
